import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { OrderPayment, VerifyPayment } from "../../services/payment.service";
import { getAllPackages } from "../../services/packages.service";
import { profileDetails } from "../../services/user.service";
import Swal from "sweetalert2";
import { SWAL_SETTINGS, ROLE, SOCKET_EVENTS } from "../../../../utils/Constants";
import { handleServerValidations, secToHours, getUser, setLocalKey } from "../../../../utils/commonfunction";
import { useNavigate } from "react-router-dom";
import { SOCKET_CLIENT } from "../../../../utils/socket";

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }
    const script = document.createElement("script");
    script.src = "https://checkout.razorpay.com/v1/checkout.js";
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

const Packages = () => {
  const { t } = useTranslation();
  const [data, setData] = useState({ list: [] });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [balance, setBalance] = useState(0);
  const [activeTab, setActiveTab] = useState("Hours");
  const [isLoaded, setIsLoaded] = useState(false);
  const userData = getUser(ROLE.ADVERTISER) || getUser(ROLE.USER) || {};
  const navigate = useNavigate();

  useEffect(() => {
    loadRazorpayScript().then((loaded) => setIsLoaded(loaded));
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await profileDetails({ _id: userData._id });
      const userProfile = response?.data || response || {};
      setBalance(userProfile);

      // Merge updated hours into both keys — preserving whichever holds the auth token
      const balanceUpdate = {
        remaining_hours: typeof userProfile.remaining_hours === "number" ? userProfile.remaining_hours : 0,
        total_hours: typeof userProfile.total_hours === "number" ? userProfile.total_hours : 0,
        used_hours: typeof userProfile.used_hours === "number" ? userProfile.used_hours : 0,
        remaining_ai_clicks: userProfile.remaining_ai_clicks ?? 0,
        total_ai_clicks: userProfile.total_ai_clicks ?? 0,
        used_ai_clicks: userProfile.used_ai_clicks ?? 0,
      };
      const advertiserKey = getUser(ROLE.ADVERTISER) || {};
      const userKey = getUser(ROLE.USER) || {};
      setLocalKey(ROLE.ADVERTISER, JSON.stringify({ ...advertiserKey, ...balanceUpdate }));
      setLocalKey(ROLE.USER, JSON.stringify({ ...userKey, ...balanceUpdate }));
    } catch (err) {
      console.log(err);
    }
  };
  useEffect(() => {
    fetchProfile();

    const handleBalanceUpdate = (newBalance) => {
      if (newBalance) {
        setBalance(prev => ({
          ...prev,
          ...newBalance
        }));
      }
    };
    SOCKET_CLIENT.on(SOCKET_EVENTS.AI_CLICKS_UPDATED, handleBalanceUpdate);
    return () => {
      SOCKET_CLIENT.off(SOCKET_EVENTS.AI_CLICKS_UPDATED, handleBalanceUpdate);
    };
  }, []);

  const handlePayment = async (id) => {
    try {
      const resp = await OrderPayment({ packageId: id });
      const { order, paymentId, user } = resp.data;
      handlePaymentVerify(order, paymentId, id, user);
    } catch (err) {
      console.error(err);
      setError(err?.message || "Failed to fetch staff");
    } finally {
      setLoading(false);
    }
  };

  const handlePaymentVerify = async (order, paymentId, packageId, customerUser) => {
    if (!isLoaded) {
      Swal.fire({ icon: "error", text: t('advertiser.packages.razorpay_error'), ...SWAL_SETTINGS });
      return;
    }

    const currentCustomer = customerUser || userData || {};
    const customerName = currentCustomer.name || userData?.name || "";
    const customerEmail = currentCustomer.email || userData?.email || "";
    const customerPhone = currentCustomer.phone_number || currentCustomer.phone || userData?.phone_number || userData?.phone || "";

    const options = {
      key: process.env.REACT_APP_RAZORPAY_KEY_ID,
      amount: order?.amount,
      currency: order.currency,
      name: "Thela Live",
      description: "Package Purchase",
      order_id: order.id,
      prefill: {
        name: customerName,
        email: customerEmail,
        contact: customerPhone,
      },
      notes: {
        paymentId: paymentId,
        packageId: packageId,
        customer_name: customerName,
        customer_email: customerEmail,
        customer_phone: customerPhone,
      },

      method: {
        upi: true,
        card: true,
        netbanking: true,
        wallet: false,
        emi: false,
        paylater: false
      },


      handler: async (response) => {
        console.log("RAZORPAY RESPONSE", response);

        try {
          const verifyRes = await VerifyPayment({
            razorpay_order_id: response.razorpay_order_id,
            razorpay_payment_id: response.razorpay_payment_id,
            razorpay_signature: response.razorpay_signature,
            paymentId: options.notes.paymentId,
            package_id: options.notes.packageId,
          });

          console.log("VERIFY RESPONSE:", verifyRes);

          if (verifyRes.success) {
            fetchProfile();
            Swal.fire({ icon: "success", text: verifyRes.message, ...SWAL_SETTINGS });
          } else {
            Swal.fire({ icon: "error", text: handleServerValidations(response), ...SWAL_SETTINGS });
          }
        } catch (error) {
          console.log("VERIFY ERROR", error);
        }
      },
      theme: { color: "#5f63b8" },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  const fetchPackages = async () => {
    try {
      const res = await getAllPackages({});
      if (res?.success) setData(res.data);
    } catch (error) {
      console.log("CMS Fetch Error:", error);
    }
  };
  useEffect(() => {
    fetchPackages();
  }, []);

  return (
    <>
      <div className="dashbaord-bg py-10">
        <div className="container">
          {/* Header Section */}
          <div className="border-b border-gray-200 pb-[15px] mb-[30px] flex items-center justify-between flex-wrap gap-[15px]">
            <h1 className="headings-h1 text-zinc-950 font-bold text-3xl">
              {t('advertiser.packages.heading')}
            </h1>
          </div>

          {/* Balance Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 text-white">
            {/* Available Hours Card (Red Banner) */}
            <div className="bg-primary-400 p-[15px] md:p-[30px] rounded-[10px]">
              <div className="flex justify-between items-center flex-wrap gap-[15px]">
                <div>
                  <p className="headings-h1 font-semibold mb-2">
                    {t('advertiser.packages.available_hours')}
                  </p>
                  <p className="heading-web-h1 font-extrabold leading-none">{secToHours(balance?.remaining_hours || 0)}</p>
                </div>
                <button onClick={() => {
                  navigate("/advertiser/packages/purchase-history", { state: { activeTab: "Hours" } });
                }} className="btn btn-pri !bg-white !text-[#FF7E79]">
                  {t('advertiser.packages.view_history')}
                </button>
              </div>
            </div>

            {/* AI Clicks Card (Purple Banner) */}
            <div className="bg-[#5f63b8] p-[15px] md:p-[30px] rounded-[10px]">
              <div className="flex justify-between items-center flex-wrap gap-[15px]">
                <div>
                  <p className="headings-h1 font-semibold mb-2">
                    {t('advertiser.packages.ai_scan_clicks')}
                  </p>
                  <p className="heading-web-h1 font-extrabold leading-none">{balance?.remaining_ai_clicks || 0}</p>
                </div>
                <button onClick={() => {
                  navigate("/advertiser/packages/purchase-history", { state: { activeTab: "AiClicks" } });
                }} className="btn btn-pri !bg-white !text-[#5f63b8]">
                  {t('advertiser.packages.view_history')}
                </button>
              </div>
            </div>
          </div>

          {/* Tabs Section */}
          <div className="flex gap-8 border-b border-gray-200 mb-8">
            <button
              onClick={() => setActiveTab("Hours")}
              className={`pb-2 px-1 text-lg font-semibold transition-colors duration-200 ${
                activeTab === "Hours"
                  ? "border-b-2 border-primary-400 text-primary-400"
                  : "text-zinc-500 hover:text-zinc-700"
              }`}
            >
              {t('advertiser.packages.hour_packages')}
            </button>
            <button
              onClick={() => setActiveTab("AiClicks")}
              className={`pb-2 px-1 text-lg font-semibold transition-colors duration-200 ${
                activeTab === "AiClicks"
                  ? "border-b-2 border-[#5f63b8] text-[#5f63b8]"
                  : "text-zinc-500 hover:text-zinc-700"
              }`}
            >
              {t('advertiser.packages.ai_packages')}
            </button>
          </div>

          {/* Package Card Component */}
          {data?.list
            .filter((pack) => pack.Type === activeTab || (!pack.Type && activeTab === "Hours"))
            .map((pack, index) => (
            <div
              key={index}
              className="bg-white p-[15px] md:p-[20px] rounded-[10px] shadow-[0px_2px_24px_rgba(0,0,0,0.05)] mb-[30px] last:mb-0 border !border-zinc-100"
            >
              <div className="flex justify-between items-start gap-[10px]">
                <div>
                  <h3 className="heading-web-h5 text-zinc-950 mb-1">
                    {pack.Name}
                  </h3>
                  <p className="body-md text-zinc-500 mb-2">
                    {pack.Type === "AiClicks" ? `${pack.AiClicks} ${t('advertiser.packages.clicks')}` : `${pack.Hours} ${t('advertiser.packages.hrs')}`}
                  </p>
                  <p className="body-md text-zinc-800 mb-1">
                    {t('advertiser.packages.saving_money')} {pack.SavedPrice}
                  </p>
                  <p className="body-md text-zinc-500 mb-3">
                    {pack.Description}
                  </p>
                  <button className="btn btn-outline min-w-[150px]" onClick={() => handlePayment(pack._id)}>
                    {t('advertiser.packages.purchase')}
                  </button>
                </div>

                {/* Price */}
                <p className="headings-h3 text-zinc-900">{t('common.currency')} {pack.Price}</p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </>
  );
}

export default Packages;
